import { memo, useState, useCallback } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NodeData } from '@/types/workflow';
import { Brain, Copy, Trash2 } from 'lucide-react';

interface AskAINodeProps extends NodeProps {
  data: NodeData;
}

export const AskAINode = memo(({ data, selected, id }: AskAINodeProps) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const { getNodes, setNodes, getEdges, setEdges } = useReactFlow();
  const nodeColor = '#ec4899'; // Pink for AI processing

  const updateNodeData = useCallback((updates: Partial<NodeData>) => {
    const nodes = getNodes();
    const updatedNodes = nodes.map(node => {
      if (node.id === id) {
        return {
          ...node,
          data: {
            ...node.data,
            ...updates
          }
        };
      }
      return node;
    });
    setNodes(updatedNodes);
  }, [id, getNodes, setNodes]);

  const handleDuplicate = useCallback(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);
    if (currentNode) {
      const newNode = {
        ...currentNode,
        id: `${currentNode.type}_${Date.now()}`,
        position: {
          x: currentNode.position.x + 50,
          y: currentNode.position.y + 50,
        },
      };
      setNodes([...nodes, newNode]);
    }
  }, [id, getNodes, setNodes]);

  const handleDelete = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    
    const updatedNodes = nodes.filter(node => node.id !== id);
    const updatedEdges = edges.filter(edge => edge.source !== id && edge.target !== id);
    
    setNodes(updatedNodes);
    setEdges(updatedEdges);
  }, [id, getNodes, getEdges, setNodes, setEdges]);

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setShowToolbar(true)}
      onMouseLeave={() => setShowToolbar(false)}
    >
      {/* Toolbar */}
      {(showToolbar || selected) && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-1 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg px-2 py-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-muted"
              onClick={handleDuplicate}
            >
              <Copy className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 text-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={handleDelete}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      <Card 
        className={`min-w-[350px] backdrop-blur-md bg-background/75 border-pink-500/30 transition-all duration-200 ${
          selected 
            ? 'ring-2 ring-pink-500/50 shadow-lg shadow-pink-500/20' 
            : 'shadow-md hover:shadow-lg'
        }`}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center">
              <Brain className="w-4 h-4 text-pink-400" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm">{data.label || 'Ask AI'}</div>
              <div className="text-xs text-muted-foreground">Prompt an AI language model</div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0 space-y-3">
          <div>
            <Label className="text-xs">AI Provider</Label>
            <Select 
              value={data.provider || 'openai'}
              onValueChange={(value) => updateNodeData({ provider: value })}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="anthropic">Anthropic</SelectItem>
                <SelectItem value="google">Google</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Model</Label>
            <Select 
              value={data.model || 'gpt-4o-mini'}
              onValueChange={(value) => updateNodeData({ model: value })}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                {data.provider === 'openai' && (
                  <>
                    <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
                    <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                    <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                  </>
                )}
                {data.provider === 'anthropic' && (
                  <>
                    <SelectItem value="claude-3-haiku">Claude 3 Haiku</SelectItem>
                    <SelectItem value="claude-3-sonnet">Claude 3 Sonnet</SelectItem>
                    <SelectItem value="claude-3-opus">Claude 3 Opus</SelectItem>
                  </>
                )}
                {data.provider === 'google' && (
                  <>
                    <SelectItem value="gemini-pro">Gemini Pro</SelectItem>
                    <SelectItem value="gemini-1.5-pro">Gemini 1.5 Pro</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">System Prompt</Label>
            <Textarea
              placeholder="You are a helpful AI assistant that processes CRM data..."
              value={data.systemPrompt || ''}
              onChange={(e) => updateNodeData({ systemPrompt: e.target.value })}
              className="min-h-[60px] resize-none text-xs"
            />
          </div>

          <div>
            <Label className="text-xs">User Prompt Template</Label>
            <Textarea
              placeholder="Analyze this lead data and provide insights: {input}"
              value={data.userPrompt || ''}
              onChange={(e) => updateNodeData({ userPrompt: e.target.value })}
              className="min-h-[80px] resize-none text-xs"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Temperature</Label>
              <Input
                type="number"
                min="0"
                max="2"
                step="0.1"
                placeholder="0.7"
                value={data.temperature || 0.7}
                onChange={(e) => updateNodeData({ temperature: parseFloat(e.target.value) })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Max Tokens</Label>
              <Input
                type="number"
                min="1"
                max="4000"
                placeholder="1000"
                value={data.maxTokens || 1000}
                onChange={(e) => updateNodeData({ maxTokens: parseInt(e.target.value) })}
                className="h-8"
              />
            </div>
          </div>

          <div className="p-2 bg-pink-50 dark:bg-pink-950/20 rounded-md border border-pink-200 dark:border-pink-800">
            <div className="text-xs text-pink-700 dark:text-pink-300">
              💡 Use {`{input}`} to reference data from previous nodes
            </div>
          </div>
        </CardContent>
        
        {/* Input Handle */}
        <Handle
          type="target"
          position={Position.Left}
          id="input"
          className="custom-handle"
        />
        
        {/* Output Handle */}
        <Handle
          type="source"
          position={Position.Right}
          id="output"
          className="custom-handle"
          style={{
            backgroundColor: '#ec4899',
            boxShadow: '0 2px 8px rgba(236, 72, 153, 0.3)',
          }}
        />
      </Card>
    </div>
  );
});

AskAINode.displayName = 'AskAINode';